import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CAMPAIGN_STATUSES, dateOnly, money, pct, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Campaigns() {
  const { can, user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [confirm, setConfirm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["campaigns", page, q, status],
    queryFn: () => get("/campaigns", { page, page_size: 20, q: q || undefined, status: status || undefined }),
    refetchInterval: 8000,
  });

  const action = useMutation({
    mutationFn: ({ id, verb, reason }) =>
      api.post(`/campaigns/${id}/${verb}`, { confirm: true, reason }),
    onSuccess: (_, variables) => {
      toast.success(`Campaign ${variables.verb} request applied`);
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/campaigns/${id}`),
    onSuccess: () => {
      toast.success("Campaign deleted");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const clone = useMutation({
    mutationFn: (id) => api.post(`/campaigns/${id}/clone`, {}),
    onSuccess: ({ data }) => {
      toast.success("Campaign cloned as draft");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      navigate(`/campaigns/${data.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const runVerb = (row, verb, needsConfirm) => {
    if (needsConfirm) {
      setConfirm({
        id: row.id,
        verb,
        title: `${title(verb)} “${row.name}”?`,
        description:
          verb === "start"
            ? "Calls will begin according to the campaign schedule, concurrency and budget limits."
            : verb === "restart"
              ? "The same campaign starts again. Contacts that were already called go back in the queue. Do-not-call numbers stay blocked."
              : verb === "stop"
                ? "Queued calls are cancelled and active calls end. This cannot be undone."
                : "The campaign will be archived and hidden from the active list.",
      });
      return;
    }
    action.mutate({ id: row.id, verb });
  };

  const columns = [
    {
      key: "name",
      label: "Campaign",
      render: (r) => (
        <Link to={`/campaigns/${r.id}`} className="font-medium text-slate-800 hover:text-blue-600" data-testid={`campaign-link-${r.id}`}>
          {r.name}
        </Link>
      ),
    },
    { key: "product", label: "Product" },
    { key: "country", label: "Country" },
    { key: "language", label: "Lang", render: (r) => (r.language || "").toUpperCase() },
    { key: "agent_name", label: "AI agent", render: (r) => r.agent_name || "—" },
    { key: "phone_number", label: "Number", render: (r) => <span className="font-mono text-xs">{r.phone_number || "—"}</span> },
    { key: "contact_count", label: "Contacts" },
    {
      key: "progress",
      label: "Progress",
      render: (r) => (
        <div className="flex w-28 items-center gap-2">
          <Progress value={r.progress || 0} className="h-1.5" />
          <span className="w-8 text-right font-mono text-xs text-slate-500">{r.progress || 0}%</span>
        </div>
      ),
    },
    { key: "answer_rate", label: "Answer", render: (r) => pct(r.answer_rate) },
    { key: "interested_count", label: "Interested" },
    { key: "cost", label: "Cost", render: (r) => money(r.cost) },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} testId={`campaign-status-${r.id}`} /> },
    { key: "started_at", label: "Started", render: (r) => dateOnly(r.started_at) },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {can("campaigns", "write") && ["completed", "stopped", "failed", "archived"].includes(r.status) ? (
            <Button
              size="sm"
              className="h-7 bg-blue-600 px-2 text-white hover:bg-blue-700"
              onClick={() => runVerb(r, "restart", true)}
              data-testid={`campaign-restart-button-${r.id}`}
            >
              Restart
            </Button>
          ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`campaign-actions-${r.id}`} className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44 border-slate-200 bg-white shadow-md">
            <DropdownMenuItem onClick={() => navigate(`/campaigns/${r.id}`)} data-testid={`campaign-view-${r.id}`}>
              View
            </DropdownMenuItem>
            {can("campaigns", "write") ? (
              <>
                <DropdownMenuItem onClick={() => navigate(`/campaigns/${r.id}?edit=1`)}>Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => clone.mutate(r.id)} data-testid={`campaign-clone-${r.id}`}>
                  Clone
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {["draft", "scheduled", "paused"].includes(r.status) ? (
                  <DropdownMenuItem onClick={() => runVerb(r, "start", true)} data-testid={`campaign-start-${r.id}`}>
                    Start
                  </DropdownMenuItem>
                ) : null}
                {r.status === "draft" ? (
                  <DropdownMenuItem onClick={() => runVerb(r, "schedule")}>Schedule</DropdownMenuItem>
                ) : null}
                {r.status === "running" ? (
                  <DropdownMenuItem onClick={() => runVerb(r, "pause")} data-testid={`campaign-pause-${r.id}`}>
                    Pause
                  </DropdownMenuItem>
                ) : null}
                {r.status === "paused" ? (
                  <DropdownMenuItem onClick={() => runVerb(r, "resume")} data-testid={`campaign-resume-${r.id}`}>
                    Resume
                  </DropdownMenuItem>
                ) : null}
                {["completed", "stopped", "failed", "archived"].includes(r.status) ? (
                  <DropdownMenuItem onClick={() => runVerb(r, "restart", true)} data-testid={`campaign-restart-${r.id}`}>
                    Restart
                  </DropdownMenuItem>
                ) : null}
                {["running", "paused", "scheduled"].includes(r.status) ? (
                  <DropdownMenuItem
                    className="text-rose-600"
                    onClick={() => runVerb(r, "stop", true)}
                    data-testid={`campaign-stop-${r.id}`}
                  >
                    Stop
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onClick={() => runVerb(r, "archive", true)} data-testid={`campaign-archive-${r.id}`}>
                  Archive
                </DropdownMenuItem>
              </>
            ) : null}
            {isAdmin ? (
              <DropdownMenuItem
                className="text-rose-600"
                onClick={() => setConfirm({
                  id: r.id,
                  verb: "delete",
                  title: `Delete “${r.name}”?`,
                  description: r.status === "running"
                    ? "Stop the campaign first. A running campaign cannot be deleted."
                    : "The campaign leaves the list. Contacts and call history stay. Queued calls for it are cancelled.",
                })}
                data-testid={`campaign-delete-${r.id}`}
              >
                Delete
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        testId="campaigns-page"
        title="Campaigns"
        subtitle="Configure, launch and control outbound AI calling campaigns"
        actions={
          can("campaigns", "write") ? (
            <Button asChild className="bg-blue-600 hover:bg-blue-700" data-testid="new-campaign-button">
              <Link to="/campaigns/new">New campaign</Link>
            </Button>
          ) : null
        }
      />

      <Section
        testId="campaigns-section"
        title={`${data?.total ?? 0} campaigns`}
        actions={
          <div className="flex gap-2">
            <Input
              data-testid="campaigns-search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search campaigns"
              className="h-9 w-48"
            />
            <Select
              value={status || "all"}
              onValueChange={(v) => {
                setStatus(v === "all" ? "" : v);
                setPage(1);
              }}
            >
              <SelectTrigger data-testid="campaigns-status-filter" className="h-9 w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {CAMPAIGN_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{title(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      >
        <DataTable
          testId="campaigns-table"
          rowTestId="campaign-row"
          loading={isLoading}
          columns={columns}
          rows={data?.items || []}
          empty={{
            title: "No campaigns yet",
            description: "Create your first campaign with the step-by-step wizard.",
            action: (
              <Button asChild size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700">
                <Link to="/campaigns/new">Create campaign</Link>
              </Button>
            ),
          }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm?.title || ""}
        description={confirm?.description || ""}
        destructive={confirm?.verb === "stop" || confirm?.verb === "delete"}
        confirmLabel={title(confirm?.verb || "confirm")}
        onConfirm={async () => {
          if (confirm.verb === "delete") await remove.mutateAsync(confirm.id);
          else await action.mutateAsync({ id: confirm.id, verb: confirm.verb });
          setConfirm(null);
        }}
        testId="campaign-confirm-dialog"
      />
    </>
  );
}
